import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import debounce from 'utils/debounce';
import { logger } from 'utils/logger';

export const AUTOCOMPLETE_STATUS = {
  IDLE: 'IDLE',
  LOADING: 'LOADING',
  OK: 'OK',
  ZERO_RESULTS: 'ZERO_RESULTS',
  ERROR: 'ERROR',
} as const;

export type AutocompleteStatus = (typeof AUTOCOMPLETE_STATUS)[keyof typeof AUTOCOMPLETE_STATUS];
export interface FormattedSuggestion {
  original: google.maps.places.AutocompleteSuggestion;
  mainText: string;
  secondaryText: string;
  placeId: string;
}

interface AutocompleteProps {
  isScriptLoaded: boolean;
  debounceDelay?: number;
}

interface SearchConfig {
  types: string[];
}

const getPredictionText = (prediction: google.maps.places.PlacePrediction) => {
  const predictionData = prediction;
  const mainText = predictionData?.mainText?.toString() || prediction.text?.toString() || '';
  const secondaryText = predictionData?.secondaryText?.toString() || '';

  return { mainText, secondaryText };
};

export const useGoogleAutocomplete = ({
  isScriptLoaded,
  debounceDelay = 300,
}: AutocompleteProps) => {
  const [suggestions, setSuggestions] = useState<FormattedSuggestion[]>([]);
  const [status, setStatus] = useState<AutocompleteStatus>(AUTOCOMPLETE_STATUS.IDLE);
  const [isPlacesLibLoaded, setIsPlacesLibLoaded] = useState(false);
  const sessionToken = useRef<google.maps.places.AutocompleteSessionToken | null>(null);

  const refreshSessionToken = useCallback(() => {
    const PlacesLib = window.google?.maps?.places;

    if (PlacesLib?.AutocompleteSessionToken) {
      sessionToken.current = new PlacesLib.AutocompleteSessionToken();
    }
  }, []);

  useEffect(() => {
    if (!isScriptLoaded || typeof google === 'undefined') {
      return;
    }

    const initGooglePlaces = async () => {
      try {
        await google.maps.importLibrary('places');
        refreshSessionToken();
        setIsPlacesLibLoaded(true);
      } catch (e) {
        logger.error('Error loading Google Places library:', e);
        setStatus(AUTOCOMPLETE_STATUS.ERROR);
      }
    };

    initGooglePlaces();
  }, [isScriptLoaded, refreshSessionToken]);

  const performSearch = useCallback(
    async (value: string, config: SearchConfig) => {
      if (!value?.trim()) {
        return;
      }

      const AutocompleteLib = window.google?.maps?.places?.AutocompleteSuggestion;

      if (!isPlacesLibLoaded || !AutocompleteLib) {
        return;
      }

      setStatus(AUTOCOMPLETE_STATUS.LOADING);

      try {
        if (!sessionToken.current) {
          refreshSessionToken();
        }

        const request = {
          input: value,
          sessionToken: sessionToken.current || undefined,
          includedRegionCodes: ['us'],
          includedPrimaryTypes: config.types,
        };

        const { suggestions: rawSuggestions } =
          await AutocompleteLib.fetchAutocompleteSuggestions(request);
        const formattedSuggestions: FormattedSuggestion[] = rawSuggestions
          .map((item) => {
            const prediction = item.placePrediction;

            if (!prediction || !prediction.placeId) {
              return null;
            }

            const { mainText, secondaryText } = getPredictionText(prediction);

            if (!mainText.trim() && !secondaryText.trim()) {
              return null;
            }

            return {
              original: item,
              mainText,
              secondaryText,
              placeId: prediction.placeId,
            };
          })
          .filter((item): item is FormattedSuggestion => item !== null);

        setSuggestions(formattedSuggestions);
        setStatus(
          formattedSuggestions.length ? AUTOCOMPLETE_STATUS.OK : AUTOCOMPLETE_STATUS.ZERO_RESULTS,
        );

        return formattedSuggestions;
      } catch (error) {
        logger.error('Google Maps Autocomplete Error:', error);
        setStatus(AUTOCOMPLETE_STATUS.ERROR);

        return [];
      }
    },
    [isPlacesLibLoaded, refreshSessionToken],
  );

  const performSearchRef = useRef(performSearch);

  useEffect(() => {
    performSearchRef.current = performSearch;
  }, [performSearch]);

  const fetchSuggestions = useMemo(
    () =>
      debounce(
        (value: string, config: SearchConfig) => performSearchRef.current(value, config),
        debounceDelay,
      ),
    [debounceDelay],
  );

  useEffect(() => {
    return () => {
      fetchSuggestions.cancel();
    };
  }, [fetchSuggestions]);

  const clearSuggestions = useCallback(() => {
    setSuggestions([]);
    setStatus(AUTOCOMPLETE_STATUS.IDLE);
  }, []);

  return {
    suggestions,
    status,
    isPlacesLibReady: isPlacesLibLoaded,
    sessionToken,
    fetchSuggestions,
    clearSuggestions,
    refreshSessionToken,
  };
};
